import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import { Controller, useForm } from "react-hook-form";
import IntlCurrencyInput from "react-intl-currency-input";
import { useExpenseCategories } from "../../../hooks/useExpenseCategories";

const currencyConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

function toInputDate(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function todayInputDate() {
  return toInputDate(new Date());
}

function buildPayload(values, categories) {
  const category = categories?.find(
    (item) => String(item.id) === String(values.expense_category_id)
  );
  const amount = Number(values.value) || 0;
  const isPaid = Boolean(values.mark_as_paid);

  return {
    description: values.description?.trim() || null,
    due_date: values.due_date,
    value: amount,
    total_value: amount,
    fee: 0,
    late_fine: 0,
    extra_value: 0,
    confirmed_date: isPaid
      ? values.confirmed_date || todayInputDate()
      : null,
    category: category
      ? { value: category.id, label: category.title }
      : null,
    expense_category_id: category?.id ?? null,
  };
}

export default function FormDespesa({
  show,
  onHide,
  expense,
  onSubmit,
  isSubmitting,
}) {
  const isEdit = Boolean(expense?.id);
  const { expenseCategories, isFetching: isLoadingCategories } =
    useExpenseCategories();
  const [valueAmount, setValueAmount] = useState(0);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      description: "",
      expense_category_id: "",
      due_date: "",
      value: 0,
      mark_as_paid: false,
      confirmed_date: todayInputDate(),
    },
  });

  const markAsPaid = watch("mark_as_paid");

  useEffect(() => {
    if (!show) return;

    if (expense) {
      const amount = Number(expense.value) || 0;
      setValueAmount(amount);
      reset({
        description: expense.description || "",
        expense_category_id: expense.expense_category_id
          ? String(expense.expense_category_id)
          : "",
        due_date: toInputDate(expense.due_date),
        value: amount,
        mark_as_paid: Boolean(expense.confirmed_date),
        confirmed_date: expense.confirmed_date
          ? toInputDate(expense.confirmed_date)
          : todayInputDate(),
      });
      return;
    }

    setValueAmount(0);
    reset({
      description: "",
      expense_category_id: "",
      due_date: "",
      value: 0,
      mark_as_paid: false,
      confirmed_date: todayInputDate(),
    });
  }, [show, expense, reset]);

  const submit = async (values) => {
    const payload = buildPayload(values, expenseCategories);
    await onSubmit(payload);
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton className="modalGradient">
        <Modal.Title className="text-white">
          {isEdit ? "Editar despesa" : "Nova despesa"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(submit)}>
          <Form.Group className="mb-3">
            <FloatingLabel label="Descrição">
              <Form.Control
                type="text"
                placeholder="Descrição"
                {...register("description", { required: true })}
              />
            </FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <FloatingLabel label="Categoria">
              <Form.Select
                {...register("expense_category_id", { required: true })}
                disabled={isLoadingCategories}
              >
                <option value="">Selecione...</option>
                {expenseCategories?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <FloatingLabel label="Valor">
              <Controller
                control={control}
                name="value"
                rules={{ required: true, min: 0.01 }}
                render={({ field }) => (
                  <IntlCurrencyInput
                    currency="BRL"
                    config={currencyConfig}
                    className="form-control"
                    value={valueAmount}
                    onChange={(event, value) => {
                      setValueAmount(value);
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <FloatingLabel label="Vencimento">
              <Form.Control
                type="date"
                {...register("due_date", { required: true })}
              />
            </FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="mark-as-paid"
              label="Marcar como paga (entra no fluxo de caixa)"
              checked={Boolean(markAsPaid)}
              onChange={(event) => {
                const checked = event.target.checked;
                setValue("mark_as_paid", checked);
                if (checked) {
                  setValue("confirmed_date", todayInputDate());
                }
              }}
            />
          </Form.Group>

          {markAsPaid && (
            <Form.Group className="mb-3">
              <FloatingLabel label="Data do pagamento">
                <Form.Control
                  type="date"
                  {...register("confirmed_date", { required: markAsPaid })}
                />
              </FloatingLabel>
            </Form.Group>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="outline-secondary" type="button" onClick={onHide}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="btn-bg-primary text-dark borderColor"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Salvando...
                </>
              ) : isEdit ? (
                "Salvar"
              ) : (
                "Cadastrar"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
